'use client';

import { useEffect, useState } from 'react';
import { useHydrate } from '@benzinga/hooks';
import { usePermission, useUserSubscriptions } from '@benzinga/user-context';

import { readNavChromeBootServices } from '../navChromeBoot';
import type { PremiumServiceAccess } from '../premiumServiceLinks';

export const BOOT_ACCESS_GRACE_MS = 5000;

export const usePremiumServiceAccess = (isLoggedIn: boolean): PremiumServiceAccess => {
  const subscriptions = useUserSubscriptions();
  const hasProDashboard = usePermission('bzpro/dashboard/open', '#');
  const hasResearchAccess = usePermission('trade-alerts/access', 'true');
  const hasEdgeSubscription = usePermission('com/read', 'unlimited-calendars');
  const hydrated = Boolean(useHydrate(true, false));
  const [graceOver, setGraceOver] = useState(false);

  const accessPending = isLoggedIn && hydrated && subscriptions === undefined && !graceOver;

  useEffect(() => {
    if (!accessPending) {
      return;
    }
    const timer = setTimeout(() => setGraceOver(true), BOOT_ACCESS_GRACE_MS);
    return () => clearTimeout(timer);
  }, [accessPending]);

  const boot = accessPending ? readNavChromeBootServices() : null;

  const hasAccessibleSub = (check: (product: string, basePlan: string) => boolean) =>
    subscriptions?.some(
      sub => (sub.status === 'active' || sub.status === 'trialing') && check(sub.product, sub.basePlan ?? ''),
    ) ?? false;

  return {
    hasEdge:
      isLoggedIn &&
      (Boolean(boot?.edge) ||
        hasEdgeSubscription ||
        hasAccessibleSub((_product, basePlan) => basePlan.toLowerCase().includes('benzinga-edge'))),
    hasPro:
      isLoggedIn && (Boolean(boot?.pro) || hasProDashboard || hasAccessibleSub(product => product === 'benzinga-pro')),
    hasResearch:
      isLoggedIn &&
      (Boolean(boot?.research) ||
        hasResearchAccess ||
        hasAccessibleSub(product => ['benzinga-newsletter', 'benzinga-plus', 'benzinga-premium'].includes(product))),
  };
};
